/**
 * 상태 버킷 → 점 색. 색을 문자열로 조립하면 Tailwind가 클래스를 못 찾으므로 표로 둔다.
 * 키는 `features/*`의 상태 타입을 import하지 않고 문자열로 받는다 — shared가 특정 기능을 알면
 * 그 기능 없이는 shared를 못 쓰게 된다. 모르는 값은 대기색으로 떨어진다.
 */
const DOT_TONE: Record<string, string> = {
  todo: 'bg-todo',
  progress: 'bg-progress',
  done: 'bg-done',
  dropped: 'bg-dropped',
  unknown: 'bg-unknown',
};

export function Chip({
  children,
  tone,
}: {
  children: React.ReactNode;
  tone?: 'repo' | 'warn';
}): React.JSX.Element {
  const skin =
    tone === 'repo'
      ? 'border-accent-line bg-accent-soft text-[#0a3069]'
      : tone === 'warn'
        ? 'border-amber-200 bg-amber-50 text-amber-800'
        : 'border-line bg-surface text-muted';
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-1.5 text-[11px] whitespace-nowrap ${skin}`}>
      {children}
    </span>
  );
}

export function StatusDot({ status }: { status: string }): React.JSX.Element {
  return <span className={`mr-1.5 inline-block size-[7px] rounded-full ${DOT_TONE[status] ?? 'bg-todo'}`} />;
}

export function Avatar({ name }: { name: string }): React.JSX.Element {
  return (
    <span
      title={name}
      className="grid size-[22px] place-items-center rounded-full bg-line-soft text-[10px] font-bold text-muted"
    >
      {name.slice(0, 2).toUpperCase()}
    </span>
  );
}

export function Bar({ percent }: { percent: number }): React.JSX.Element {
  return (
    <span className="block h-1.5 overflow-hidden rounded-full bg-line-soft">
      <i className="block h-full rounded-full bg-done" style={{ width: `${percent}%` }} />
    </span>
  );
}

export function EmptyState({ message }: { message: string }): React.JSX.Element {
  return (
    <div className="rounded-lg border border-dashed border-line bg-surface p-10 text-center text-muted">
      {message}
    </div>
  );
}

/** 카드 안 KPI 숫자 — 값 색만 톤으로 바꾼다(위험할수록 눈에 띄게). */
export function Stat({
  label,
  value,
  hint,
  suffix,
  tone,
}: {
  label: string;
  value: React.ReactNode;
  hint: string;
  suffix?: string;
  tone?: 'warn' | 'critical' | 'good';
}): React.JSX.Element {
  const skin =
    tone === 'critical' ? 'text-rose-700' : tone === 'warn' ? 'text-amber-700' : tone === 'good' ? 'text-done' : '';
  return (
    <div className="rounded-lg border border-line bg-surface px-4 py-3">
      <div className="text-[11px] font-semibold tracking-wide text-muted uppercase">{label}</div>
      <div className={`mt-1 text-2xl font-bold tabular-nums ${skin}`}>
        {value}
        {suffix && <span className="text-base">{suffix}</span>}
      </div>
      <div className="text-[11px] text-faint">{hint}</div>
    </div>
  );
}

/** 대메뉴 화면들이 공통으로 쓰는 섹션 카드 — 제목·부가설명·툴바·우측 액션. */
export function Card({
  title,
  hint,
  action,
  toolbar,
  children,
}: {
  title: string;
  hint?: string;
  action?: { label: string; onClick: () => void };
  toolbar?: React.ReactNode;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <section className="rounded-lg border border-line bg-surface p-4">
      <header className="mb-3 flex flex-wrap items-baseline gap-2">
        <h2 className="text-[13px] font-bold">{title}</h2>
        {hint && <span className="text-[11px] text-faint">{hint}</span>}
        {toolbar && <span className="ml-2">{toolbar}</span>}
        {action && (
          <button onClick={action.onClick} className="ml-auto cursor-pointer text-[11.5px] text-accent hover:underline">
            {action.label} →
          </button>
        )}
      </header>
      {children}
    </section>
  );
}
